import { type CSSProperties, type FC, useCallback, useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Flex, Button, Spinner, Text, PinInput, PinInputField, HStack } from '@eqi-investimentos/react';
import { BadgeErrorForm } from '@/modules/Auth/ui/BadgeErrorForm';
import { formatTime } from '@/utils/date';
import { IfRender } from '@/utils/jsx';
import { Aeonik } from '@/themes/fonts';
export const OtpCodeInput: FC<OtpCodeInputProps> = ({
  loading = false,
  error = '',
  otpValidationTime = 120,
  isDisabled = false,
  handleCode,
  handleExpiration,
  resendSendOtp
}) => {
  const {
    t
  } = useTranslation('common');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [remainingTime, setRemainingTime] = useState(otpValidationTime);
  const [isError, setIsError] = useState(false);
  const startTimer = () => {
    intervalRef.current = setInterval(() => {
      setRemainingTime(prevTime => {
        if (prevTime <= 1 && intervalRef?.current) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };
  const handleResetCode = () => {
    resendSendOtp();
    setIsError(false);
    setRemainingTime(otpValidationTime);
    startTimer();
  };
  const memoizedHandleExpiration = useCallback(() => {
    if (handleExpiration) {
      handleExpiration();
    }
  }, [handleExpiration]);
  useEffect(() => setIsError(!!error), [error]);
  useEffect(() => {
    startTimer();
    return () => {
      intervalRef?.current && clearInterval(intervalRef.current);
    };
  }, []);
  useEffect(() => {
    if (remainingTime === 0) {
      memoizedHandleExpiration();
    }
  }, [remainingTime, memoizedHandleExpiration]);
  return <Flex flexDir="column" alignItems="center" my="4" h="auto" w="full" sx={{
    input: {
      outline: 'none !important',
      borderColor: isError ? 'alert.error' : 'border.primary',
      _focus: {
        outline: 'none !important',
        borderColor: isError ? 'alert.error' : 'border.primary',
        boxShadow: 'none'
      },
      '.code-input-otp': {
        fontFamily: Aeonik.style.fontFamily,
        fontWeight: 'normal'
      }
    }
  }} data-sentry-element="Flex" data-sentry-component="OtpCodeInput" data-sentry-source-file="index.tsx">
      <HStack data-sentry-element="HStack" data-sentry-source-file="index.tsx">
        <PinInput isDisabled={isDisabled} autoFocus manageFocus errorBorderColor="alert.error" onComplete={handleCode} data-sentry-element="PinInput" data-sentry-source-file="index.tsx">
          {Array.from({
          length: 6
        }, (_, index) => <PinInputField key={index} />)}
        </PinInput>
      </HStack>

      <IfRender condition={!!isError} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
        <Flex alignItems="center" mt="8" w="95%" borderRadius="lg" transition="all 300ms ease-in-out" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
          <BadgeErrorForm htmlError={error ?? ''} data-sentry-element="BadgeErrorForm" data-sentry-source-file="index.tsx" />
        </Flex>
      </IfRender>

      {loading && <Flex data-testid="container__spinner_loading" flexDir="column" alignItems="center" mt="10" w="full" h="1.5625rem" my="8" sx={{
      '.chakra-spinner': {
        color: 'brand.600 !important',
        w: '1.375rem !important',
        h: '1.375rem !important'
      }
    }}>
          <Spinner />
        </Flex>}

      {!loading && <>
          <Button onClick={remainingTime > 0 ? handleResetCode : () => null} variant="naked" mt="3xl" mb="0.625rem" opacity={remainingTime ? 0.45 : 1} transition="all 300ms ease-in-out">
            {t('screens.otp.resendCode')}
          </Button>
          {remainingTime > 0 && <Text data-testid="otp-code-input-time-remaining" size="xs" color="content.primary" transition="all 300ms ease-in-out">
              {formatTime(remainingTime)}
            </Text>}
        </>}
    </Flex>;
};
type OtpCodeInputProps = {
  inputStyle?: CSSProperties;
  loading?: boolean;
  error?: string | null;
  otpValidationTime?: number;
  isDisabled?: boolean;
  handleCode: (code: string) => void;
  handleExpiration?: () => void;
  resendSendOtp: () => void;
};